
import {
    Checkbox,
    DefaultButton,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Stack,
    Text,
    TextField
} from "@fluentui/react";
import { RootState } from "app/store";
import { fadeIn, panelButtonsStyles, suggestNewCESNumber } from "global/Application";
import { ArrayToArrayByPredicateFlat } from "global/Utility/TSFunctions";
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IGroupStructureCESEditorProps } from "./IGroupStructureCESEditorProps.type";
import { IGroupStructureCESWizardItem } from "../GroupStructureCESWizard/IGroupStructureCESWizardItem.type";
import { updateCESNumber } from "global/Reducer/dataSlice";
import SectionItem, { ESectionItemVariant } from "components/controls/SectionItem/Index";


const GroupStructureCESEditor: React.FunctionComponent<IGroupStructureCESEditorProps> = (props) => {

    const dispatch = useDispatch();
    const state = useSelector((state: RootState) => state);

    const disabled = props.disabled;

    const [value, setValue] = useState<FAM.ICESNumber>(props.value
        ? structuredClone(props.value)
        : suggestNewCESNumber(state.data.json.groupStructure.cesNumbers)
    );

    const list = useMemo(() => state.data.json.groupStructure.cesNumbers.map(eachCesNumber => {
        const t: IGroupStructureCESWizardItem = {
            oldCES: { ...eachCesNumber },
            newCES: { ...eachCesNumber },
            status: null,
        }
        return t;
    }), [state.data.json.groupStructure.cesNumbers]);


    useEffect(() => {
        if (props.isOpen) {
            setValue(props.value
                ? structuredClone(props.value)
                : suggestNewCESNumber(state.data.json.groupStructure.cesNumbers)
            );
        }
    }, [props.isOpen, props.value]);


    const validateCES = (cesNumbers: FAM.ICESNumber[]) => {
        const listGroupByCESNumber = ArrayToArrayByPredicateFlat(cesNumbers, t => t.cesNumber);
        const listGroupById = ArrayToArrayByPredicateFlat(cesNumbers, t => t.id);
        const hasConflictingCES = cesNumbers.some(l1 => cesNumbers.some(l2 => l1.cesNumber === l2.cesNumber && !l1.deactivated && l2.deactivated));

        return { listGroupByCESNumber, listGroupById, hasConflictingCES }
    }

    const { newList, hasConflictingCES, listGroupByCESNumber, listGroupById } = useMemo(() => {
        let newList = structuredClone(list);

        if (props.isAdd)
            newList.push({
                newCES: value,
                oldCES: null,
                status: 'untracked'
            });
        else
            newList = newList.map((each, index) => {
                if (each.oldCES?.cesNumber === value.cesNumber || each.oldCES?.id === value.id)
                    return {
                        ...each,
                        newCES: {
                            ...each.newCES,
                            ...value
                        }
                    }
                return each;
            });

        const result = validateCES(newList.map(t => t.newCES));

        return { newList, ...result };
    }, [value])

    const onChange = (value: FAM.ICESNumber) => {

        dispatch(updateCESNumber(newList));
        props.onDismiss();
    }

    const onRender = () => {

        if (!value)
            return <div>Unexpected state</div>;

        return <SectionItem title="Properties" variant={ESectionItemVariant.Drawer}>
            <Stack tokens={{ childrenGap: 5 }} styles={{ root: { marginTop: '8px' } }}>

                <Stack tokens={{ childrenGap: 5 }}>
                    <Stack.Item>
                        <TextField
                            label="CES Client Number"
                            value={value.cesNumber}
                            disabled={disabled}
                            onChange={(event, newValue) => setValue(x => ({ ...x!, cesNumber: newValue ?? '' }))}
                        />
                    </Stack.Item>
                    <Stack.Item align="end">
                        <Checkbox
                            label="Deactivate"
                            disabled={disabled}
                            checked={value.deactivated ?? false}
                            onChange={(event, checked) => setValue(x => ({ ...x!, deactivated: checked ?? false }))}
                        />
                    </Stack.Item>
                </Stack>

                {listGroupByCESNumber.some(t => t.length > 1) &&
                    <Text variant="small">
                        🔔 We have identified multiple instances of shared CES numbers in our records. Should you choose to proceed, the system will initiate a merge process to rectify these duplicates.
                    </Text>
                }

                {listGroupById.some(t => t.length > 1) &&
                    <Text variant="small">
                        🔔 We have identified multiple instances of CES numbers sharing same GUIDs in our records. Should you choose to proceed, the system will initiate a merge process to rectify these duplicates.
                    </Text>

                }

                {hasConflictingCES &&
                    <Text variant="small">
                        ⛔ We have identified conflicting configuration, Please check the deactivated CES Numbers and Active CES Number
                    </Text>
                }
            </Stack>
        </SectionItem>

    }

    const onRenderFooter = () => {

        return <div className={fadeIn}>
            <PrimaryButton
                text='Continue'
                disabled={hasConflictingCES || disabled}
                styles={panelButtonsStyles}
                onClick={(event) => {
                    onChange(value)
                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={() => { props.onCancel(); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onDismiss}
        type={PanelType.medium}
        layerProps={{ eventBubblingEnabled: true }}
        headerText="Manage | Group Structure"
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
    >
        <Separator />
        {onRender()}
    </Panel>
}

export default GroupStructureCESEditor;